// store/refSessionStore.ts
import { create } from "zustand";
import { persist } from "zustand/middleware";

// ----------------------------------------
// 1) Generic KV store (Zustand + persist + TTL)
// ----------------------------------------

interface KVItem {
  value: string;
  expiresAt?: number; // timestamp ms
}

interface KVState {
  items: Record<string, KVItem>;
  setValue: (key: string, value: string, ttlMs?: number) => void;
  getValue: (key: string) => string | undefined;
  removeValue: (key: string) => void;
  clear: () => void;
}

/**
 * Yeh internal store hai:
 * - Zustand + persist
 * - name: "bbps-kv-store" (localStorage ke andar, but via middleware)
 * - TTL support: getValue pe expiry check + cleanup
 *
 * Iska naam "refSessionKVStore" hai — hook nahi hai, aur React rules-of-hooks ko confuse nahi karega.
 */
export const refSessionKVStore = create<KVState>()(
  persist(
    (set, get) => ({
      items: {},

      setValue: (key, value, ttlMs) => {
        const expiresAt = ttlMs ? Date.now() + ttlMs : undefined;
        set((state) => ({
          items: {
            ...state.items,
            [key]: { value, expiresAt },
          },
        }));
      },

      getValue: (key) => {
        const item = get().items[key];
        if (!item) return undefined;

        if (item.expiresAt && item.expiresAt < Date.now()) {
          // expired -> cleanup
          set((state) => {
            const copy = { ...state.items };
            delete copy[key];
            return { items: copy };
          });
          return undefined;
        }

        return item.value;
      },

      removeValue: (key) => {
        set((state) => {
          const copy = { ...state.items };
          delete copy[key];
          return { items: copy };
        });
      },

      clear: () => set({ items: {} }),
    }),
    {
      name: "bbps-kv-store", // <- persist key in storage
      partialize: (state) => ({ items: state.items }),
    },
  ),
);

// ----------------------------------------
// 2) Domain wrapper: refId / plans per biller
// ----------------------------------------

type RefKind = "billFetch" | "billPayment" | "validation" | "plans";

const PREFIX = "refSession";

function makeKey(kind: RefKind, billerId: string, extra?: string) {
  // example: refSession:billFetch:OU12BB000NATKB
  return extra
    ? `${PREFIX}:${kind}:${billerId}:${extra}`
    : `${PREFIX}:${kind}:${billerId}`;
}

/**
 * Domain level utility:
 * - raw helpers for generic key/value
 * - typed helpers tumhare BBPS flows ke liye
 *
 * Isko saga, services, React component, kahin se bhi use kar sakte ho:
 *   refSessionStore.setBillFetchRef(billerId, refId, 5 * 60_000);
 *   const refId = refSessionStore.getBillFetchRef(billerId);
 */
export const refSessionStore = {
  // ---------- Raw helpers (generic KV) ----------

  setRaw(key: string, value: string, ttlMs?: number) {
    const { setValue } = refSessionKVStore.getState();
    setValue(key, value, ttlMs);
  },

  getRaw(key: string): string | undefined {
    const { getValue } = refSessionKVStore.getState();
    return getValue(key);
  },

  removeRaw(key: string) {
    const { removeValue } = refSessionKVStore.getState();
    removeValue(key);
  },

  clearAll() {
    const { clear } = refSessionKVStore.getState();
    clear();
  },

  // ---------- Typed helpers for tumhara flow ----------

  /** billFetch ke liye refId (per biller) */
  setBillFetchRef(billerId: string, refId: string, ttlMs?: number) {
    const key = makeKey("billFetch", billerId);
    const { setValue } = refSessionKVStore.getState();
    setValue(key, refId, ttlMs);
  },

  getBillFetchRef(billerId: string): string | undefined {
    const key = makeKey("billFetch", billerId);
    const { getValue } = refSessionKVStore.getState();
    return getValue(key);
  },

  clearBillFetchRef(billerId: string) {
    const key = makeKey("billFetch", billerId);
    const { removeValue } = refSessionKVStore.getState();
    removeValue(key);
  },

  /** billPayment ke liye refId (alag rakho, same biller ke multiple flows possible) */
  setBillPaymentRef(billerId: string, refId: string, ttlMs?: number) {
    const key = makeKey("billPayment", billerId);
    const { setValue } = refSessionKVStore.getState();
    setValue(key, refId, ttlMs);
  },

  getBillPaymentRef(billerId: string): string | undefined {
    const key = makeKey("billPayment", billerId);
    const { getValue } = refSessionKVStore.getState();
    return getValue(key);
  },

  clearBillPaymentRef(billerId: string) {
    const key = makeKey("billPayment", billerId);
    const { removeValue } = refSessionKVStore.getState();
    removeValue(key);
  },

  /** billValidation ke refId agar future me chahiye */
  setValidationRef(billerId: string, refId: string, ttlMs?: number) {
    const key = makeKey("validation", billerId);
    const { setValue } = refSessionKVStore.getState();
    setValue(key, refId, ttlMs);
  },

  getValidationRef(billerId: string): string | undefined {
    const key = makeKey("validation", billerId);
    const { getValue } = refSessionKVStore.getState();
    return getValue(key);
  },

  clearValidationRef(billerId: string) {
    const key = makeKey("validation", billerId);
    const { removeValue } = refSessionKVStore.getState();
    removeValue(key);
  },

  /** Agar plan select ke saath bhi kuch cache rakhna ho (last selected plan) */
  setPlanRef(billerId: string, planId: string, ttlMs?: number) {
    const key = makeKey("plans", billerId);
    const { setValue } = refSessionKVStore.getState();
    setValue(key, planId, ttlMs);
  },

  getPlanRef(billerId: string): string | undefined {
    const key = makeKey("plans", billerId);
    const { getValue } = refSessionKVStore.getState();
    return getValue(key);
  },

  clearPlanRef(billerId: string) {
    const key = makeKey("plans", billerId);
    const { removeValue } = refSessionKVStore.getState();
    removeValue(key);
  },
};

// how to use
// // saga ya thunk me:
// const refId = response.data.refId;
// refSessionStore.setBillFetchRef(billerId, refId, 10 * 60_000); // 10 mins TTL
// const refId = refSessionStore.getBillFetchRef(billerId);
// refSessionStore.setBillFetchRef("OU12BB000NATKB", "ref-fetch-1");
// refSessionStore.setBillPaymentRef("OU12BB000NATKB", "ref-pay-1");
// refSessionStore.setValidationRef("PREP00000NAT8K", "ref-val-1");
// refSessionStore.setPlanRef("NPCIPRESINAT02", "plan-id-123");

// if want hook
// // src/features/biller/hooks/useRefIdStore.ts
// import { refSessionStore } from "@/shared/storage/refSessionStore";

// export function useRefIdStore() {
//   return {
//     getBillFetchRef: (billerId: string) =>
//       refSessionStore.get<string>(`billFetch:${billerId}`),

//     setBillFetchRef: (billerId: string, refId: string) =>
//       refSessionStore.set(`billFetch:${billerId}`, refId),

//     getBillPaymentRef: (billerId: string) =>
//       refSessionStore.get<string>(`billPayment:${billerId}`),

//     setBillPaymentRef: (billerId: string, refId: string) =>
//       refSessionStore.set(`billPayment:${billerId}`, refId),
//   };
// }
